import { useEffect, useRef, useState } from "react";
import { integrationHostDataSchema } from "@proof/core";
import {
  registerProofForm,
  formDataBindingsSchema,
  type FormDataBinding,
  type ProofFormElement,
  type FormConfiguration,
} from "@proof/sdk";
import { Button, Control } from "./components";
registerProofForm();
export function StandaloneFormData({
  definition,
}: {
  definition: FormConfiguration;
}) {
  const element = useRef<ProofFormElement>(null);
  const [bindings, setBindings] = useState<FormDataBinding[]>([]);
  const [sample, setSample] = useState(
    '{"directory":{"record-1":{"name":"Example customer"}}}',
  );
  const [error, setError] = useState("");
  const [applied, setApplied] = useState<
    Record<string, Record<string, Record<string, unknown>>>
  >({});
  const [delivery, setDelivery] = useState("proof");
  const active = bindings.filter((binding) =>
    definition.fields.some((field) => field.id === binding.fieldId),
  );
  const bindingJson = JSON.stringify(active);
  useEffect(() => {
    const form = element.current;
    if (!form) return;
    form.dataProvider = async (query, signal) => {
      if (signal.aborted) throw Error("Cancelled");
      const values =
        integrationHostDataSchema.parse(applied)[query.sourceId]?.[
          query.recordId
        ];
      if (!values) throw Error("No sample record");
      return {
        version: 1,
        sourceId: query.sourceId,
        recordId: query.recordId,
        values: Object.fromEntries(
          query.fields.map((key) => {
            if (!Object.hasOwn(values, key))
              throw Error("Missing sample field");
            return [key, values[key]];
          }),
        ),
      };
    };
    const parsed = formDataBindingsSchema.safeParse(JSON.parse(bindingJson));
    if (!parsed.success) {
      setError(
        "Complete the source, field, and record identifiers before previewing.",
      );
      return;
    }
    form.dataBindings = parsed.data;
    form.form = definition;
  }, [definition, bindingJson, applied]);
  const edit = (fieldId: string, update: Partial<FormDataBinding>) =>
    setBindings((current) => {
      const old = current.find((binding) => binding.fieldId === fieldId) || {
        fieldId,
        sourceId: "directory",
        recordId: "record-1",
        sourceField: "name",
      };
      return [
        ...current.filter((binding) => binding.fieldId !== fieldId),
        { ...old, ...update },
      ];
    });
  const snippet = `<script src="${location.origin}/embed.js"></script>
<div id="form-host"></div>
<script>
(async () => {
  // Your backend authenticates the customer and chooses permitted records.
  const response = await fetch('/your-server/form-session', {method:'POST', credentials:'same-origin'});
  if (!response.ok) throw new Error('Form session unavailable');
  const session = await response.json();
  const form = document.createElement('proof-form');
  form.form = session.definition; // Standalone form-configuration JSON.
  ${
    delivery === "proof"
      ? `form.headers = {Authorization: 'Bearer ' + session.access_token};
  form.setAttribute('api-url', ${JSON.stringify(location.origin)});`
      : `form.dataProvider = async (query, signal) => {
    const response = await fetch('/your-server/form-data', {
      method:'POST', credentials:'same-origin', signal,
      headers:{'Content-Type':'application/json'}, body:JSON.stringify(query)
    });
    if (!response.ok) throw new Error('Form data unavailable');
    return response.json(); // {version:1,sourceId,recordId,values}
  };`
  }
  form.dataBindings = session.bindings; // Your backend authorizes every binding.
  form.addEventListener('proof:change', event => {
    // Your backend validates submissions and routes them to your projects.
    saveThroughYourHost(event.detail.values);
  });
  document.getElementById('form-host').append(form);
})().catch(() => { document.getElementById('form-host').textContent = 'Form unavailable'; });
</script>`;
  return (
    <section className="panel padded" aria-label="Standalone form data">
      <h2>Drop-in form &amp; external data</h2>
      <p>
        This form is independent of a document. Your backend can connect it to
        any number of projects or data sources. Configure the form above, then
        choose which external fields feed its controls.
      </p>
      {!definition.fields.length && (
        <p>
          Add fields in the form configuration editor to configure their data
          bindings.
        </p>
      )}
      {definition.fields.map((field) => {
        const binding = active.find((binding) => binding.fieldId === field.id);
        return (
          <fieldset key={field.id} className="panel padded">
            <legend>{field.label || field.key}</legend>
            <label>
              <input
                type="checkbox"
                checked={!!binding}
                onChange={(e) =>
                  e.target.checked
                    ? edit(field.id, {})
                    : setBindings((current) =>
                        current.filter(
                          (binding) => binding.fieldId !== field.id,
                        ),
                      )
                }
              />{" "}
              Use external data for {field.label || field.key}
            </label>
            {binding && (
              <div className="control-grid">
                <Control label="Binding source ID">
                  <input
                    value={binding.sourceId}
                    onChange={(e) =>
                      edit(field.id, { sourceId: e.target.value })
                    }
                  />
                </Control>
                <Control label="Binding source field">
                  <input
                    value={binding.sourceField}
                    onChange={(e) =>
                      edit(field.id, { sourceField: e.target.value })
                    }
                  />
                </Control>
                <Control label="Sample record ID">
                  <input
                    value={binding.recordId}
                    onChange={(e) =>
                      edit(field.id, { recordId: e.target.value })
                    }
                  />
                </Control>
              </div>
            )}
            <p className="muted">
              {field.editable && !field.system
                ? "Users can edit this field. External data supplies its initial value or permitted choices."
                : "This field is read-only. Its value comes from the supplied data."}
            </p>
          </fieldset>
        );
      })}
      <Control label="Form data delivery">
        <select value={delivery} onChange={(e) => setDelivery(e.target.value)}>
          <option value="proof">
            Proof Studio data grants and external API connections
          </option>
          <option value="host">
            Your authenticated backend provides form data directly
          </option>
        </select>
      </Control>
      <p>
        For Proof Studio delivery, configure each source under Access &amp;
        tokens. Your server submits <code>data_grants</code> when issuing the
        form token. Backend-supplied records use <code>host_data</code>;
        connected HTTPS sources are queried on the server using encrypted
        credentials.
      </p>
      <details>
        <summary>Current binding contract</summary>
        <pre>{JSON.stringify(active, null, 2)}</pre>
      </details>
      <Control label="Sample external records (JSON)">
        <textarea
          rows={8}
          value={sample}
          onChange={(e) => setSample(e.target.value)}
        />
      </Control>
      <p className="muted">
        Use source ID → record ID → field keys. This local sandbox does not save
        records or contact configured external services. Choice fields accept
        the typed dynamic-choices catalog.
      </p>
      {error && <p role="alert">{error}</p>}
      <Button
        onClick={() => {
          try {
            const data = integrationHostDataSchema.parse(JSON.parse(sample));
            formDataBindingsSchema.parse(active);
            setApplied(data);
            setError("");
          } catch {
            setError(
              "Check the sample JSON and source, field, and record identifiers.",
            );
          }
        }}
      >
        Apply sample data
      </Button>
      <div className="panel padded">
        <h3>Standalone form preview</h3>
        <proof-form ref={element} />
      </div>
      <h3>Drop-in setup</h3>
      <pre>{snippet}</pre>
      <Button onClick={() => void navigator.clipboard.writeText(snippet)}>
        Copy standalone form snippet
      </Button>
    </section>
  );
}
